import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Reviewer } from '@deltek/specification-client/core/Configurations/Authorization'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import ProductCategories from '@deltek/specification-client/core/Constants/productCategories'
import ProductListingLevels from '@deltek/specification-client/core/Constants/productListingLevels'
import { AppHeaderContext } from '@deltek/specification-client/core/Contexts/AppHeaderContext'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import {
    ListingMatch,
    ListingSearchInput,
    LogEventSource,
} from '@deltek/specification-client/core/Models/listingSearch'
import { ProductTypeSelectionInput } from '@deltek/specification-client/core/Models/productType'
import { SearchFacet } from '@deltek/specification-client/core/Models/searchFacet'
import { SearchFacetInput } from '@deltek/specification-client/core/Models/searchFacetInput'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import ProductSearchBar from '@deltek/specification-client/core/Widgets/ProductListing/Filter/ProductSearchBar'
import { NormalizedProductType } from '@deltek/specification-client/core/Widgets/ProductListing/model'
import { sortList } from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import {
    SpecColumn,
    SpecIdType,
} from '@deltek/specification-client/core/Widgets/SpecTable'
import { PinningProps } from '@deltek/specification-client/core/Widgets/SpecTable/settings/TableSettings'
import { FirmNameQuery, FirmQuery } from 'bpm/api/graphQL/queries/firm.queries'
import { ListingsGetListingProductTypeProperties } from 'bpm/api/graphQL/queries/product.queries'
import {
    ProductSearchAutosuggestQuery,
    ProductSearchQuery,
} from 'bpm/api/graphQL/queries/productSearch.queries'
import { FirmResponse } from 'bpm/api/graphQL/types/firm.types'
import {
    ProductPropertyDetailResponse,
    ProductSearchAutosuggestResponse,
    ProductSearchResponse,
} from 'bpm/api/graphQL/types/product.types'

import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { IdType } from 'react-table'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeGrid } from 'react-window'
import { columns, defaultColumns } from '../../TablePage/tablePage'
import ProductHeader from '../components/ProductHeader'
import {
    createFilter,
    getFilterDisabledValues,
    getSelectedProductTypesFromQueryString,
    getSelectedValuesFromQueryString,
    isDeltekUserorAdmin,
    isUserBPMAdmin,
    updateQueryString,
    UserType,
} from '../productsUtils'
import { queryNameToDateParamMap } from '../queryNameMaps'
import { ProductListingResultGrid } from './components/ProductListingResultGrid'
import { useProductListingStyle } from './ProductLisitngsPage.styles'
import AccordionWindow from './ProductListingGroupingTree'

// A.K.A Family Name
//value = section name and product type
export const getSectionName = (value?: string) =>
    value ? value.split('%|%')?.[0]?.slice(1, -1) : ''

//value = section name and product type
export const getProductTypeName = (value?: string) =>
    value ? value.split('%|%')?.[1]?.slice(1, -1) : ''

export const PER_PAGE = 200

export const getFacetInputsFromFilterSelection = (
    filterSelectionData: Record<string, string[]> | undefined
): SearchFacetInput[] => {
    let facets: SearchFacetInput[] = []
    const filterSelection: Record<string, string[]> | undefined = {
        ...filterSelectionData,
    }
    for (let facetName in filterSelection) {
        let facetValues: string[] = filterSelection[facetName]
        if (
            facetValues &&
            facetValues.length &&
            facetName !== 'additionalContent' &&
            facetName !== 'orderBy'
        ) {
            facets = [
                ...facets,
                {
                    name: facetName,
                    values: [...facetValues],
                },
            ]
        }
    }
    return facets
}

export const escapeElementDelimiter = (plaintext: string): string => {
    return plaintext.replace(/,/gi, '\\u002c')
}
export const unescapeElementDelimiter = (escapetext: string): string => {
    return escapetext.replace(/\\u002c/gi, ',')
}

export const getInputSelectionValuesFromQueryString = (
    querystring: string
): Record<string, Date | string | null> => {
    let inputSelectionValues: Record<string, Date | string | null> = {}
    let queryParams = new URLSearchParams(querystring)

    for (let index in queryNameToDateParamMap) {
        let val = queryParams.get(queryNameToDateParamMap[index].queryName)
        if (val) {
            inputSelectionValues[
                queryNameToDateParamMap[index].facetName
            ] = decodeURIComponent(unescapeElementDelimiter(val))
        }
    }
    return inputSelectionValues
}

const isAdditionalContentSelected = (
    additionalContentFilter: string[],
    catValue: string
): boolean | undefined => {
    let isSelected: boolean = false
    if (additionalContentFilter && additionalContentFilter.length) {
        isSelected = additionalContentFilter.some(
            (category: string) => category === catValue
        )
    }
    if (isSelected) return true
    return undefined
}
interface CurrentDropDownInput {
    filterName: string
    inputValue: string
}

export const OPTIMISTIC_CARDS = 5
const ACTIVATETABLEVIEW = false

const emptySelectionObject: Record<string, string[]> = {
    categories: [],
    listingLevels: [],
    partnerships: [],
    submissionStatus: [],
    isActive: [],
    displayNameWithFirmName: [],
    sectionNames: [],
    additionalContent: [],
    sectionNameWithProductType: [],
}

const emptyInputSelectionObject: Record<string, Date | string | null> = {
    lastUpdatedEndDate: null,
    lastUpdatedStartDate: null,
    lastUpdatedInPastDays: null,
}
const defaultSortOrder = 'sectionNumber,productType,type,lastUpdated desc'
const defaultQuery =
    'groupby=sectionNumber false,productType false&sortby=sectionNumber,productType,type,lastUpdated desc&ia=true&defaultFilter=true'
const defaultfilterParam = 'df'
const usersFirmFilterParam = 'myFirm'

const ProductListingsPage: React.FC = () => {
    const {
        updateAppHeaderContent,
        updateAppHeaderStyles,
        hasLowerBottomToolbar,
    } = React.useContext(AppHeaderContext)

    const history = useHistory()
    const { location } = history
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )

    const { isDeltekUser, isDeltekAdmin } = useMemo(
        () => isDeltekUserorAdmin(currentUserInfoResponse),
        [currentUserInfoResponse]
    )

    const { isBPM } = useMemo(() => isUserBPMAdmin(currentUserInfoResponse), [
        currentUserInfoResponse,
    ])

    const accountType =
        currentUserInfoResponse?.currentUserInfo.account.accountType
    const accountId = currentUserInfoResponse?.currentUserInfo.account.accountId
    const [getFirmNameInfo, { data: currentFirmNameInfo }] = useLazyQuery<any>(
        FirmNameQuery,
        { fetchPolicy: 'no-cache', nextFetchPolicy: 'no-cache' }
    )

    const { refetch: refetchFirmName } = useQuery<FirmResponse>(FirmQuery, {
        variables: {
            id:
                currentUserInfoResponse?.currentUserInfo?.account?.accountId ??
                '',
        },
        skip: !currentUserInfoResponse,
    })

    const [listingMatchDataResults, setListingMatchDataResults] = useState<
        Partial<ListingMatch>[] | undefined
    >()

    const [isCardViewActive, setToggleCardView] = useState<boolean>(true)

    const classes = useProductListingStyle({ hasLowerBottomToolbar })

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [selectedValues, setSelectedValues] = useState<
        Record<string, string[]> | undefined
    >({
        ...emptySelectionObject,
        ...getSelectedValuesFromQueryString(location.search),
    })

    const [selectedInputValues, setSelectedInputValues] = useState<
        Record<string, Date | string | null> | undefined
    >({
        ...emptyInputSelectionObject,
        ...getInputSelectionValuesFromQueryString(location.search),
    })
    const assignedCompanyRef = useRef<string>()
    const [facets, setFacets] = React.useState<SearchFacetInput[]>([
        ...getFacetInputsFromFilterSelection(selectedValues),
    ])

    const [currentDropDownName, setCurrentDropDownName] = useState<string>('')

    const [currentDropDownInput, setCurrentDropDownInput] = useState<
        CurrentDropDownInput
    >({ filterName: '', inputValue: '' })

    const [scrollState, setScrollState] = React.useState({
        rowIndex: 0,
        columnIndex: 0,
    })

    const [columnCount, setColumnCount] = React.useState<number>(0)

    const [refetch, setRefetch] = React.useState<boolean>(false)

    const [hasNextPage, setHasNextPage] = React.useState<boolean>(true)

    const [
        filteringOnAssignedFirm,
        setFilteringOnAssignedFirm,
    ] = React.useState<boolean>(false)

    const infiniteLoaderRef = React.useRef(null)

    const hasMountedRef = React.useRef(false)

    const [accordionTreeRef, setAccordionTreeRef] = React.useState<
        React.RefObject<HTMLElement> | HTMLElement
    >()

    const [productTypeSelections, setProductTypeSelections] = React.useState<
        NormalizedProductType[]
    >([...getSelectedProductTypesFromQueryString(location.search)])

    const [currentTotal, setCurrentTotal] = React.useState<number>(PER_PAGE)

    const [searchFetchMoreLoading, setSearchFetchMoreLoading] = React.useState<
        boolean
    >(false)

    const [searchLoading, setSearchLoading] = React.useState<boolean>(false)
    const [tableFilterView, setTableFilterView] = useState<boolean>(true)

    const tableColumns = useMemo(() => [...defaultColumns, ...columns], [])
    const [visibileColumns, setVisibleColumns] = useState([...defaultColumns])
    const [hiddenColumns, setHiddenColumns] = useState(
        columns.map((col) => col?.id)
    )
    const [pinningProps, setPinningProps] = useState<PinningProps>({
        firstColumnPinned: false,
        lastColumnPinned: false,
    })

    const [tableGrouping, setTableGrouping] = useState<
        SpecIdType<ListingMatch>[]
    >(['sectionName', 'productType'])

    const [
        manufacturerProductSearchInput,
        setManufacturerProductSearchInput,
    ] = React.useState<Partial<ListingSearchInput>>({
        skip: 0,
        top: PER_PAGE,
        orderBy: defaultSortOrder, // TODO apply user's sort preferences
        logEventSource: LogEventSource.BpmSearchListings,
    })

    const [
        getListings,
        {
            data: searchData,
            fetchMore: searchFetchMore,
            called: searchCalled,
            loading: listingLoading,
            networkStatus: listingNetworkStatus,
        },
    ] = useLazyQuery<ProductSearchResponse>(ProductSearchQuery, {
        displayName: 'ProductSearchResult',
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
        onCompleted: () => setSearchLoading(false),
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `ProductListingsPage_ProductSearchError`,
                },
            })
            setSearchLoading(false)
        },
    })

    const [productCount, setProductCount] = useState<number>(0)

    const { loading: autosuggestLoading, data: autosuggestData } = useQuery<
        ProductSearchAutosuggestResponse
    >(ProductSearchAutosuggestQuery, {
        variables: {
            input: {
                facets: createFilter(
                    facets,
                    'AutoSuggest',
                    currentDropDownName
                ).filter(
                    (facet) => facet.name !== currentDropDownName
                    // don't limit autosuggest by what's already selected in same filter
                ),
                searchLimit: 50,
                searchField: currentDropDownName,
                searchText: currentDropDownInput.inputValue,
                productTypeSelections:
                    currentDropDownName !== 'sectionNameWithProductType'
                        ? productTypeSelections.map(
                              (each) =>
                                  ({
                                      productTypeProperties:
                                          each.productTypeProperties,
                                      sectionName: each.sectionName,
                                      productTypeName: each.productTypeName,
                                  } as ProductTypeSelectionInput)
                          )
                        : [],
            },
        },
        skip: !currentDropDownName, // only run query if an autosuggest drop-down is open
        fetchPolicy: 'no-cache',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product autosuggest',
                    variant: 'error',
                    id: `ProductListingsPage_ProductAutosuggestError`,
                },
            })
        },
    })

    const [onSelected, setOnSelected] = React.useState<boolean>(false)

    const [
        getListingProductTypeProperties,
        {
            loading: loadingSelectedProductType,
            error: errorSelectedProductType,
            data: dataSelectedProductType,
        },
    ] = useLazyQuery<ProductPropertyDetailResponse>(
        ListingsGetListingProductTypeProperties,
        {
            displayName: 'GetListingProductTypeProperties',
            onError: (error) => {
                showGlobalSnackbar?.({
                    variables: {
                        message: summarizeApolloError(
                            error,
                            'Error fetching product type properties.'
                        ),
                        variant: 'error',
                        id:
                            'ProductTypeFilter_ListingProductTypePropertiesError',
                    },
                })
            },
            onCompleted: () => setOnSelected(true),
        }
    )
    const [resultFacetLookup, setResultFacetLookup] = React.useState<
        Record<string, string[]>
    >({})

    const [filterValues, setFilterValues] = React.useState<
        Record<string, string[]>
    >({})
    const [skip, setSkip] = useState<number>(PER_PAGE)

    const [groupBy, setGroupBy] = useState<Record<string, boolean | string>>({})
    const sortRef = React.useRef<string>('')

    const handleAutoSuggestDropdown = (value: string) => {
        setCurrentDropDownName(value)
        setFilterValues((past) => ({
            ...past,
            sectionNames: [],
            sectionNameWithProductType: [],
            displayNameWithFirmName: [],
        }))
    }

    const setScrollRowAndColum = React.useCallback(
        (rowIndex: number, columnIndex: number) => {
            setScrollState({ rowIndex, columnIndex })
        },
        []
    )

    const isItemLoaded = (index: number) => !!listingMatchDataResults?.[index]
    const handleClearAll = () => {
        const search = `?${defaultQuery}`

        history.replace({
            ...history.location,
            search,
        })
        setSelectedInputValues({
            ...emptyInputSelectionObject,
            ...getInputSelectionValuesFromQueryString(search),
        })
        setProductTypeSelections([
            ...getSelectedProductTypesFromQueryString(search),
        ])
        const updatedSelectedValues = {
            ...emptySelectionObject,
            ...getSelectedValuesFromQueryString(search),
        }
        setSelectedValues(updatedSelectedValues)
        setFacets([...getFacetInputsFromFilterSelection(updatedSelectedValues)])
    }

    const handleLoadMore = React.useCallback(
        (_startIndex: number, stopIndex: number) => {
            if (
                !searchLoading &&
                !searchFetchMoreLoading &&
                !refetch &&
                searchData?.listingSearch?.count &&
                Math.ceil(currentTotal / columnCount) - stopIndex > 0 &&
                hasNextPage &&
                !!searchFetchMore
            ) {
                setSearchFetchMoreLoading(true)
                setCurrentTotal((prev) => prev + OPTIMISTIC_CARDS)

                return searchFetchMore({
                    variables: {
                        manufacturerProductSearchInput: {
                            ...manufacturerProductSearchInput,
                            skip: searchData.listingSearch.matches?.length,
                            top: PER_PAGE,
                        },
                    },
                }).then(() => setSearchFetchMoreLoading(false))
            }
            return null
        },
        [
            manufacturerProductSearchInput,
            searchLoading,
            searchFetchMoreLoading,
            refetch,
            searchData,
            currentTotal,
            columnCount,
            hasNextPage,
            searchFetchMore,
        ]
    )

    const handleTableOrder = React.useCallback(
        (columnOrder: IdType<any>[]) => {
            const newColumns = tableColumns
                ?.filter((col) =>
                    columnOrder?.find((newCol) => newCol === col.id)
                )
                .map((column) => {
                    return {
                        ...column,
                        index: columnOrder?.findIndex(
                            (col) => col === column.id
                        ),
                    }
                })
                .sort((a, b) => a.index - b.index)
            setVisibleColumns(newColumns)
        },
        [tableColumns]
    )

    const handleColumnRemove = React.useCallback(
        (hiddenColumns: IdType<any>[]) => {
            setHiddenColumns(hiddenColumns as string[])
        },
        []
    )

    const handlePinningProps = React.useCallback((response: PinningProps) => {
        setPinningProps(response)
    }, [])

    const handleTableGrouping = React.useCallback((tableGrouping: any[]) => {
        setTableGrouping(tableGrouping?.map((group) => group.id))
    }, [])

    // Refetch
    const doRefetch = React.useCallback(() => {
        getListings({
            variables: {
                manufacturerProductSearchInput: {
                    ...manufacturerProductSearchInput,
                    facets: manufacturerProductSearchInput?.facets,
                },
            },
        })
        setRefetch(false)
        setSearchLoading(true)
    }, [getListings, manufacturerProductSearchInput])

    useEffect(() => {
        const { isBPM } = isUserBPMAdmin(currentUserInfoResponse)
        const searchParams = new URLSearchParams(location.search)

        if (
            !isBPM ||
            searchParams.has(defaultfilterParam) ||
            !searchParams?.has('defaultFilter') ||
            !currentFirmNameInfo?.firm?.name
        )
            return

        const assignedCompany = escapeElementDelimiter(
            `'${currentFirmNameInfo?.firm?.displayName}'%|%'${currentFirmNameInfo?.firm?.name}'`
        )
        searchParams.append(defaultfilterParam, assignedCompany)
        searchParams.delete('defaultFilter')

        history.replace({
            ...history.location,
            search: `?${searchParams.toString()}`,
        })

        setFilteringOnAssignedFirm(true)

        const updatedSelectedValues = {
            ...selectedValues,
            ...getSelectedValuesFromQueryString(searchParams.toString()),
        }
        if (
            isEqual(
                facets,
                getFacetInputsFromFilterSelection(updatedSelectedValues)
            )
        )
            return

        setSelectedValues(updatedSelectedValues)
        setFacets([...getFacetInputsFromFilterSelection(updatedSelectedValues)])
    }, [
        currentFirmNameInfo,
        selectedValues,
        history,
        facets,
        currentUserInfoResponse,
    ])

    useEffect(() => {
        const searchParams = new URLSearchParams(history.location.search)
        if (searchParams?.get(usersFirmFilterParam) === 'true') {
            setFilteringOnAssignedFirm(true)
        }
    }, [])

    useEffect(() => {
        const searchFacets = createFacetLookup(
            searchData?.listingSearch.facets ?? []
        )
        if (searchData?.listingSearch.matches) {
            setProductCount(searchData?.listingSearch.count ?? 0)
            setResultFacetLookup(searchFacets)
            setListingMatchDataResults(
                isCardViewActive ||
                    !(
                        !isCardViewActive &&
                        listingMatchDataResults?.length! < skip
                    )
                    ? [...searchData?.listingSearch?.matches]
                    : searchData?.listingSearch?.matches?.length! ===
                          searchData?.listingSearch?.count! &&
                      searchData?.listingSearch?.count! < skip
                    ? [...searchData?.listingSearch?.matches]
                    : (Array.from(
                          new Set([
                              ...(listingMatchDataResults as Partial<
                                  ListingMatch
                              >[]),
                              ...searchData?.listingSearch?.matches,
                          ])
                      ) as Partial<ListingMatch>[])
            )
        }
    }, [searchData, location, selectedValues, isCardViewActive, skip])

    React.useEffect(() => {
        updateAppHeaderStyles?.({
            titleTextColor: '#04325B',
            backgroundColor: '#ffffff',
        })
    }, [updateAppHeaderStyles])

    React.useEffect(() => {
        if (currentUserInfoResponse) {
            if (searchData)
                updateAppHeaderContent(
                    `Product Listings (${productCount ?? 0})`,
                    <ProductHeader
                        roles={{
                            isDeltekAdmin,
                        }}
                        firmId={
                            currentUserInfoResponse.currentUserInfo.account
                                .accountId
                        }
                        showCompanyButton={isBPM}
                        showAddProductButton={isDeltekAdmin}
                        loading={false}
                    />
                )
        }
    }, [
        updateAppHeaderContent,
        searchData,
        currentUserInfoResponse,
        searchLoading,
        productCount,
        isDeltekAdmin,
        isBPM,
    ])

    React.useEffect(() => {
        if (!autosuggestLoading) {
            const autosuggestValues =
                autosuggestData?.listingAutoSuggest?.result || []
            const excludedListingLevels = ['Premium MasterSpec', 'Premium']
            setFilterValues({
                categories:
                    resultFacetLookup?.Categories?.sort() ?? ProductCategories,
                listingLevels:
                    processEnv.REACT_APP_EnableBadgelessCards === '1'
                        ? ProductListingLevels.filter(
                              (item) => !excludedListingLevels.includes(item)
                          )
                        : ProductListingLevels.filter(
                              (item) => item !== 'Product Listing'
                          ),
                displayNameWithFirmName:
                    currentDropDownName === 'displayNameWithFirmName'
                        ? autosuggestValues
                        : [],
                sectionNames:
                    currentDropDownName === 'sectionNames'
                        ? autosuggestValues
                        : [],
                sectionNameWithProductType:
                    currentDropDownName === 'sectionNameWithProductType'
                        ? autosuggestValues
                        : [],
            })
        }
    }, [
        autosuggestData,
        autosuggestLoading,
        currentDropDownName,
        resultFacetLookup,
    ])

    useEffect(() => {
        const { isBPM } = isUserBPMAdmin(currentUserInfoResponse)
        if (!currentFirmNameInfo && accountId && isBPM)
            getFirmNameInfo({
                variables: {
                    firmId: accountId,
                },
            })
    }, [
        currentUserInfoResponse,
        currentFirmNameInfo,
        getFirmNameInfo,
        location,
        accountId,
    ])

    // Used to reset the scrollbar
    React.useEffect(() => {
        if (hasMountedRef.current) {
            if (infiniteLoaderRef) {
                const list = infiniteLoaderRef.current as any
                if (list) {
                    const grid = list._listRef as FixedSizeGrid
                    list.resetloadMoreItemsCache() //Reset infinite scroll
                    grid.scrollToItem({
                        align: 'start',
                        columnIndex: 0,
                        rowIndex: 0,
                    }) //scroll to top after refresh
                    list.scrollTop = 0 //firefox solution for scroll not reseting
                }
            }
        }
        hasMountedRef.current = true
    }, [refetch, searchLoading])

    React.useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        if (location.search) {
            searchParams.set(
                usersFirmFilterParam,
                filteringOnAssignedFirm ? 'true' : 'false'
            )

            history.replace({
                ...location,
                search: searchParams.toString(),
            })
        }
    }, [filteringOnAssignedFirm])

    const setGroupFromUrl = useCallback(() => {
        const searchParams = new URLSearchParams(location.search)
        if (
            Object.keys(groupBy).length === 0 &&
            location.search &&
            (searchParams.has('groupby') || searchParams.has('sortby'))
        ) {
            const groupByData =
                (searchParams.has('groupby') &&
                    decodeURIComponent(searchParams.get('groupby')!)) ||
                ''
            const sortBy =
                (searchParams.has('sortby') &&
                    decodeURIComponent(searchParams.get('sortby')!)) ||
                ''
            let groupByObj: Record<string, boolean> = {}
            groupByData
                ?.split(',')
                .forEach(
                    (group) =>
                        group?.split(' ')[0] &&
                        (groupByObj[group?.split(' ')[0]] =
                            group?.split(' ')[1] === 'true')
                )
            if (sortRef.current === sortBy && isEqual(groupByObj, groupBy))
                return
            if (!isEqual(groupByObj, groupBy)) {
                setGroupBy(groupByObj)
            }
            const currentOrderby = ((sortBy && sortBy.split(',')) || []).filter(
                (sort) => !!sort
            )
            if (!isEqual(selectedValues?.orderBy, currentOrderby)) {
                setSelectedValues({
                    ...selectedValues,
                    orderBy: currentOrderby,
                })
            }
            sortRef.current = sortBy
        }
    }, [location, selectedValues, groupBy])

    React.useMemo(async () => {
        let computedFacets = createFilter(facets, 'Search')

        /*
         *  TODO:Please review it, if its needed anymore for some specific group of users
         */

        // all AE users and reviewers except Deltek (system) MUST always force active only listings
        if (
            accountType === UserType.AEfirm ||
            (accountType !== UserType.Deltek &&
                currentUserInfoResponse?.currentUserInfo.roles.includes(
                    Reviewer
                ))
        ) {
            // check if isActive is already included,
            // if true, remove it first so we don't have to worry about the value
            // then push the correct isActive = true.
            const isActiveIndex = computedFacets.findIndex(
                (facet) => facet['name'] === 'isActive'
            )
            if (isActiveIndex > -1) {
                computedFacets.splice(isActiveIndex, 1)
            }
            computedFacets.push({
                name: 'isActive',
                values: ['true'],
            })
        }

        const searchParams = new URLSearchParams(location.search)

        const staleFirmName = `'${currentFirmNameInfo?.firm?.displayName}'%|%'${currentFirmNameInfo?.firm?.name}'`
        const firmNameFacet = computedFacets.find(
            (facet) => facet['name'] === 'displayNameWithFirmName'
        )
        if (
            searchParams.get(usersFirmFilterParam) === 'true' &&
            firmNameFacet &&
            firmNameFacet.values?.length === 1
        ) {
            const { data } = await refetchFirmName()

            const updatedFirmName = `'${data?.firm?.displayName}'%|%'${data?.firm?.name}'`
            if (updatedFirmName != staleFirmName) {
                firmNameFacet.values[0] = updatedFirmName
                if (selectedValues)
                    selectedValues.displayNameWithFirmName[0] = updatedFirmName
                getFirmNameInfo({
                    variables: {
                        firmId: accountId,
                    },
                })
            }
        }

        setManufacturerProductSearchInput((past) => ({
            ...past,
            orderBy: selectedValues?.orderBy?.length
                ? selectedValues.orderBy.join(',')
                : defaultSortOrder,
            facets: computedFacets,
            lastUpdatedEndDate: selectedInputValues?.lastUpdatedEndDate
                ? selectedInputValues.lastUpdatedEndDate + 'T23:59:59.999Z'
                : undefined,
            lastUpdatedStartDate: selectedInputValues?.lastUpdatedStartDate
                ? selectedInputValues.lastUpdatedStartDate + 'T00:00:00.000Z'
                : undefined,
            lastUpdatedInPastDays: selectedInputValues?.lastUpdatedInPastDays
                ? 180
                : undefined,
            hasBimLinks: selectedValues
                ? isAdditionalContentSelected(
                      selectedValues['additionalContent'],
                      'hasBimLinks'
                  )
                : undefined,
            hasCatalogs: selectedValues
                ? isAdditionalContentSelected(
                      selectedValues['additionalContent'],
                      'hasCatalogs'
                  )
                : undefined,
            hasDocumentFL: selectedValues
                ? isAdditionalContentSelected(
                      selectedValues['additionalContent'],
                      'hasDocumentFL'
                  )
                : undefined,
            hasDocumentSF: selectedValues
                ? isAdditionalContentSelected(
                      selectedValues['additionalContent'],
                      'hasDocumentSF'
                  )
                : undefined,
            productTypeSelections: productTypeSelections.map(
                (each) =>
                    ({
                        productTypeProperties: each.productTypeProperties,
                        sectionName: each.sectionName,
                        productTypeName: each.productTypeName,
                    } as ProductTypeSelectionInput)
            ),
        }))
        setSkip(PER_PAGE)
        setRefetch(true)
        setCurrentTotal(10)
    }, [
        facets,
        selectedValues,
        productTypeSelections,
        selectedInputValues,
        currentUserInfoResponse,
        accountType,
    ])

    const createFacetLookup = (dataFacets: Partial<SearchFacet>[]) => {
        return dataFacets.reduce((result, facet) => {
            if (result && facet.name) {
                result[facet.name] =
                    facet?.values?.map((fv) => fv.value || '') || []
            }
            return result
        }, {} as Record<string, string[]>)
    }

    // on refetch change
    React.useMemo(() => {
        if (!searchLoading && refetch) {
            doRefetch()
            setHasNextPage(false)
        }
    }, [doRefetch, searchLoading, refetch])

    // on Render
    React.useMemo(() => {
        if (!searchCalled && hasMountedRef.current) {
            doRefetch()
            setHasNextPage(false)
        }
    }, [doRefetch, searchCalled, hasMountedRef])

    // Update current total based on cache
    React.useEffect(() => {
        if (
            !searchLoading &&
            !searchFetchMoreLoading &&
            !refetch &&
            !!searchData?.listingSearch?.matches &&
            !!productCount
        ) {
            const count =
                searchData?.listingSearch?.matches?.length < PER_PAGE
                    ? productCount
                    : searchData?.listingSearch?.matches?.length

            if (currentTotal !== count && searchData?.listingSearch?.count) {
                setCurrentTotal(count)
                setHasNextPage(
                    count >= PER_PAGE
                        ? count < searchData?.listingSearch?.count
                        : false
                )
            }
        }
    }, [
        searchData,
        searchLoading,
        searchFetchMoreLoading,
        currentTotal,
        refetch,
        productCount,
    ])

    useEffect(() => {
        setGroupFromUrl()
    }, [setGroupFromUrl])
    const lastGroupingInfoRef = useRef<Record<string, boolean | string>>()
    const handleLoadMoreResults = useCallback(() => {
        const nextSkip = skip + PER_PAGE
        setSearchLoading(true)
        getListings &&
            getListings({
                variables: {
                    manufacturerProductSearchInput: {
                        ...manufacturerProductSearchInput,
                        facets: manufacturerProductSearchInput.facets,
                        skip,
                        top: PER_PAGE,
                    },
                },
            })
        if (!Object.values(groupBy).includes('spLoadMore'))
            lastGroupingInfoRef.current = groupBy
        let modifiedGroupBy: Record<string, boolean | string> = {}
        Object.keys(groupBy).forEach((key) => {
            modifiedGroupBy[key] = 'spLoadMore'
        })

        if (!isEqual(groupBy, modifiedGroupBy)) setGroupBy(modifiedGroupBy)
        setSkip(nextSkip)
    }, [getListings, skip, manufacturerProductSearchInput, groupBy])

    const onFilterSelectionChangeHandler = async (
        selection: Record<string, string[]> | undefined
    ) => {
        const selectedData: Record<string, string[]> = {
            ...selection,
        }
        if (
            selectedData.orderBy?.length === 2 &&
            !isEqual(selectedData.orderBy, selectedValues?.orderBy)
        ) {
            const groupByDataObj = JSON.parse(selectedData.orderBy[0])
            const sortByDataObj = JSON.parse(selectedData.orderBy[1])
            const orderByData = [...sortByDataObj].filter(
                (fieldName) => !!fieldName
            ) as string[]
            setGroupBy(groupByDataObj)
            selectedData.orderBy = orderByData
        } else if (
            selectedData.orderBy?.length !== 2 &&
            Object.keys(groupBy).length !== 0
        )
            setGroupBy({})
        else if (Object.values(groupBy).includes('spLoadMore'))
            setGroupBy({ ...lastGroupingInfoRef.current })

        //If the company filter has changed remove the myFirm flag
        if (
            selectedData.displayNameWithFirmName !=
                selectedValues?.displayNameWithFirmName &&
            filteringOnAssignedFirm
        ) {
            setFilteringOnAssignedFirm(false)
        }

        //Check if user's firm name has changed and update the search filters if needed
        const staleFirmName = `'${currentFirmNameInfo?.firm?.displayName}'%|%'${currentFirmNameInfo?.firm?.name}'`
        if (
            selectedData.displayNameWithFirmName?.length === 1 &&
            selectedData.displayNameWithFirmName[0] === staleFirmName
        ) {
            const { data } = await refetchFirmName()
            const updatedFirmName = `'${data?.firm?.displayName}'%|%'${data?.firm?.name}'`

            if (updatedFirmName != staleFirmName) {
                selectedData.displayNameWithFirmName[0] = updatedFirmName
                getFirmNameInfo({
                    variables: {
                        firmId: accountId,
                    },
                })
            }
        }

        setSelectedValues(selectedData)
        setFacets(getFacetInputsFromFilterSelection(selectedData))
        updateQueryString(selectedData, undefined, history, location)
    }

    const handelProductTypeSelection = useCallback(
        (values: NormalizedProductType[]) => {
            const propArr: string[] = []
            values.forEach((prop) => {
                if (!!prop.productTypeProperties?.length) {
                    prop.productTypeProperties?.forEach(
                        (ptp: NormalizedProductType) =>
                            propArr.push(
                                `${prop.sectionName}%|%${
                                    prop.productTypeName
                                }%|%${
                                    ptp.propertyName
                                }:${ptp.propertyOptionIds?.join(',')}`
                            )
                    )
                } else {
                    propArr.push(
                        `${prop.sectionName}%|%${prop.productTypeName}`
                    )
                }
            })

            const productTypePropertiesQuery =
                (propArr.length && encodeURIComponent(propArr.join(';'))) || ''

            const searchParams = new URLSearchParams(location.search)

            searchParams.has('ptp') && searchParams.delete('ptp')
            if (productTypePropertiesQuery) {
                searchParams.set('ptp', productTypePropertiesQuery)
            }
            if (values.length > 0 && searchParams.has('sp')) {
                history.replace({
                    ...location,
                    search: searchParams.toString(),
                })
            }
            if (!isEqual(values, productTypeSelections)) {
                setProductTypeSelections(values)
            }
        },
        [history, location, productTypeSelections]
    )

    React.useEffect(() => {
        if (!searchLoading && accordionTreeRef) {
            const accordionTree = accordionTreeRef as HTMLElement
            const accordionTreeChild = accordionTree.firstElementChild as HTMLElement

            accordionTreeChild.setAttribute('tabIndex', '0')
            accordionTreeChild.focus()
        }
    }, [searchLoading, accordionTreeRef])

    useEffect(() => {
        const groupingColumn = visibileColumns?.filter((col) =>
            tableGrouping?.find((grouping) => grouping === col.id)
        )
        if (groupingColumn?.length !== tableGrouping?.length) {
            const columnsToAdd = tableGrouping.map((grouping) => {
                return tableColumns?.find((col) => col.id === grouping)
            }) as SpecColumn<ListingMatch>[]

            setVisibleColumns((past) => [...past, ...columnsToAdd])
        }
    }, [visibileColumns, tableGrouping, tableColumns])

    React.useEffect(() => {
        let selectedPropertyIds: Array<string> = []
        let selection = { ...productTypeSelections }
        productTypeSelections.forEach((prop, index) => {
            if (prop.productTypeProperties) {
                prop.productTypeProperties?.forEach(
                    (ptp: NormalizedProductType) => {
                        selectedPropertyIds = [
                            ...selectedPropertyIds,
                            ...(ptp.propertyOptionIds
                                ? ptp.propertyOptionIds
                                : []),
                        ]
                    }
                )
            }

            if (prop.properties) {
                prop.properties?.forEach((pr: any, ind: number) => {
                    let optionGroups: Array<any> = []
                    let options = pr.optionGroups
                        ?.map((op: any) => {
                            // change 'selected' property in optionGroups based on selected option ids
                            optionGroups.push({
                                ...op,
                                options: op.options.map((oo: any) => {
                                    return {
                                        ...oo,
                                        selected: selectedPropertyIds.includes(
                                            oo.optionId
                                        ),
                                    }
                                }),
                            })
                            return op.options
                        })
                        .flat(1)
                    // change 'selected' property in options based on selected option ids
                    options = options.map((op: any) => {
                        return {
                            ...op,
                            selected: selectedPropertyIds.includes(op.optionId),
                        }
                    })
                    // assign back the changed values
                    selection[index].properties[ind].optionGroups = optionGroups
                    selection[index].properties[ind].options = options
                })
            }
        })
    }, [])

    const disabledFilterValues = useMemo(() => {
        const hasManufacturerOptions = !!selectedValues?.withManufacturerOption?.some(
            (value) => value === 'true'
        )
        return getFilterDisabledValues(
            resultFacetLookup,
            hasManufacturerOptions
        )
    }, [selectedValues, resultFacetLookup, getFilterDisabledValues])

    return (
        <GridContainer className={classes.container} direction="column">
            <GridItem
                className={classes.productSearchContOuter}
                data-testid="productListingsPage"
            >
                <ProductSearchBar
                    filterValues={filterValues}
                    filterSelection={selectedValues}
                    filterDisabledValues={disabledFilterValues}
                    onFilterSelectionChange={onFilterSelectionChangeHandler}
                    onFilterInputChangeDebounced={(filterName, inputValue) => {
                        setCurrentDropDownInput({
                            filterName: filterName,
                            inputValue: inputValue,
                        })
                    }}
                    loadingFilterName={
                        autosuggestLoading ? currentDropDownName : ''
                    }
                    currentUserInfoResponse={currentUserInfoResponse}
                    handleEmptyInputSelection={(selection) => {
                        setSelectedInputValues(selection)
                        updateQueryString(
                            undefined,
                            selection,
                            history,
                            location
                        )
                    }}
                    emptyInputSelection={selectedInputValues}
                    handleAutoSuggestDropdown={handleAutoSuggestDropdown}
                    productTypeSelections={productTypeSelections}
                    handelProductTypeSelection={handelProductTypeSelection}
                    showGlobalSnackbar={showGlobalSnackbar}
                    resultCount={productCount ?? 0}
                    loadingSelectedProductType={loadingSelectedProductType}
                    errorSelectedProductType={errorSelectedProductType}
                    dataSelectedProductType={dataSelectedProductType}
                    getListingProductTypeProperties={
                        getListingProductTypeProperties
                    }
                    onSelected={onSelected}
                    setOnSelected={setOnSelected}
                    clearAllHandler={handleClearAll}
                    showSwatchboxFilter={
                        !!Number(processEnv.REACT_APP_ShowSwatchbox)
                    }
                />
            </GridItem>

            <GridItem className={classes.productContent}>
                {isCardViewActive ? (
                    <>
                        {!Object.keys(groupBy).length ? (
                            <ProductListingResultGrid
                                searchLoading={searchLoading}
                                searchFetchMoreLoading={searchFetchMoreLoading}
                                handleLoadMore={handleLoadMore}
                                products={listingMatchDataResults}
                                currentTotal={currentTotal}
                                hasNextPage={hasNextPage}
                                isItemLoaded={isItemLoaded}
                                scrollState={scrollState}
                                setScrollRowAndColumn={setScrollRowAndColum}
                                infiniteLoaderRef={infiniteLoaderRef}
                                setColumnCount={setColumnCount}
                            />
                        ) : !searchLoading &&
                          listingMatchDataResults?.length === 0 ? (
                            <GridContainer
                                spacing={1}
                                className={clsx(
                                    'divProductListingNoResultGrid',
                                    classes.resultContainer
                                )}
                                justifyContent="center"
                                alignContent="space-between"
                            >
                                <GridItem>
                                    <h3>No Results</h3>
                                </GridItem>
                            </GridContainer>
                        ) : (
                            <AutoSizer
                                className="accordionTree"
                                data-testid="accordion-tree"
                            >
                                {({ height, width }) => (
                                    <AccordionWindow
                                        accordionTreeRef={setAccordionTreeRef}
                                        listingCount={productCount}
                                        listings={sortList(
                                            [
                                                ...(listingMatchDataResults ??
                                                    []),
                                            ],
                                            selectedValues?.orderBy?.join(
                                                ','
                                            ) ?? ''
                                        )}
                                        searchLoading={searchLoading}
                                        onLoadMore={handleLoadMoreResults}
                                        groupBy={groupBy}
                                        height={height}
                                        width={width}
                                        listingLoading={listingLoading}
                                        isDeltekUser={isDeltekUser}
                                    />
                                )}
                            </AutoSizer>
                        )}
                    </>
                ) : (
                    <></>
                )}
            </GridItem>
        </GridContainer>
    )
}

export default ProductListingsPage
